import { SelectProps, Spin } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { Column, Flex } from '@ydistri/ds';
import { CreateAsoButton } from './CreateAsoButton';
import { SearchableSelectOptionType, selectFilterHandler } from '@ydistri/utils';
import { useGetAsoScenarioListQuery } from '../apiAso';
import { AsoScenarioResponse } from '@ydistri/api-sdk';
import {
  BackgroundVariant,
  dropdownAlign,
  OptionLeft,
  OptionRow,
  OptionTitle,
  StyledSelect,
} from './selectStyledComponents';
import { COLORS, computeRemSize } from '@ydistri/theme';

const asosSorter = (left: AsoScenarioResponse, right: AsoScenarioResponse) => left.id - right.id;

const aso2Option = (aso: AsoScenarioResponse) => ({
  value: aso.id,
  label: (
    <OptionRow>
      <OptionLeft>#{aso.id}</OptionLeft>
      <OptionTitle title={aso.name}>{aso.name}</OptionTitle>
    </OptionRow>
  ),
  searchableString: aso.name,
});

export interface AsoScenarioSelectProps {
  asoScenarioId?: number | null;
  createButtonAction?: () => void;
  backgroundVariant?: BackgroundVariant;
  onChange: (value: number) => void;
  loading?: boolean;
  placeholder?: React.ReactNode;
  asoRequestId?: number;
}

const AsoScenarioSelect: React.FC<AsoScenarioSelectProps> = ({
  onChange,
  createButtonAction,
  backgroundVariant = 'default',
  asoScenarioId,
  loading = false,
  placeholder,
  asoRequestId,
}) => {
  const { data: asoScenarios, isFetching } = useGetAsoScenarioListQuery(undefined);

  const options: SearchableSelectOptionType[] = useMemo(() => {
    if (!asoScenarios) return [];
    const filtered = asoRequestId ? asoScenarios.filter(a => a.asoRequestId === asoRequestId) : [];
    return filtered.sort(asosSorter).map(aso2Option);
  }, [asoScenarios, asoRequestId]);

  const selectStyle = useMemo(() => ({ margin: '0.5rem' }), []);

  const dropdownStyle = useMemo(
    () => ({
      popup: {
        root: {
          borderLeft: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderRight: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderBottom: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          background:
            backgroundVariant === 'transparent-background' ? COLORS.PAGE_BACKGROUND : 'white',
          boxShadow: 'none',
        },
      },
    }),
    [backgroundVariant],
  );

  const dropdownRender: SelectProps['dropdownRender'] = useCallback(
    (origin: React.ReactElement) => {
      return (
        <Column $gap="0.5rem">
          {origin}
          {createButtonAction && (
            <Flex $padding={1}>
              <CreateAsoButton title="Create ASO" onClick={createButtonAction} />
            </Flex>
          )}
        </Column>
      );
    },
    [createButtonAction],
  );

  const isDisabled = loading || !asoRequestId;
  const effectivePlaceholder =
    placeholder ?? (!asoRequestId ? 'Select ASO request first' : undefined);

  return (
    <Spin spinning={isFetching}>
      <StyledSelect
        $backgroundVariant={backgroundVariant}
        value={asoScenarioId ?? undefined}
        onChange={onChange}
        options={options}
        showSearch={true}
        variant="outlined"
        size="large"
        style={selectStyle}
        filterOption={selectFilterHandler}
        dropdownAlign={dropdownAlign}
        styles={dropdownStyle}
        popupRender={dropdownRender}
        listHeight={window.innerHeight * 0.6}
        data-testid="aso-select"
        loading={loading}
        disabled={isDisabled}
        placeholder={effectivePlaceholder}
      />
    </Spin>
  );
};

export default AsoScenarioSelect;
